<template>
  <BaseSwitchListItem
    v-model="localEnabled"
    :value="localEnabled"
    :title="feedbackTopic.title"
    :supporting-text="supportingText"
    :trailing-text="
      feedbackTopic.coachOnly
        ? t('settings.feedbackTopics.coachOnly', {
            role: t('organization.roles.coach'),
          })
        : undefined
    "
    :icon="feedbackTopic.icon"
    class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large"
  >
    <BaseDialog
      :is-open="isDisableDialogOpen"
      :title="$t('settings.feedbackTopics.disableDialog.title')"
      :description="
        $t('settings.feedbackTopics.disableDialog.description', {
          topic: feedbackTopic.title,
        })
      "
      type="basic"
      @close="cancelDisable"
    >
      <template #dismiss>
        <BaseTextButton color="text" size="M" @click="cancelDisable">
          {{ $t('common.cancel') }}
        </BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" size="M" @click="confirmDisable">
          {{ $t('settings.feedbackTopics.disableDialog.confirmAction') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </BaseSwitchListItem>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import type { OrganizationFeedbackTopicViewModel } from '../models/organization-feedback-topic'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'

  interface Props {
    feedbackTopic: OrganizationFeedbackTopicViewModel
    disableConfirmation?: boolean
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const supportingText = computed(() => {
    const lines = [props.feedbackTopic.description].filter((line): line is string => !!line)

    return lines.length > 0 ? lines : undefined
  })

  const emits = defineEmits<{
    (
      e: 'feedback-topic-enabled-updated',
      input: { feedbackTopicId: string; enabled: boolean },
    ): void
  }>()

  const { isOpen: isDisableDialogOpen, openDialog, closeDialog } = useDialog()

  const localEnabled = ref(props.feedbackTopic.enabled)

  watch(
    () => props.feedbackTopic.enabled,
    (enabled) => {
      localEnabled.value = enabled
    },
  )

  watch(localEnabled, (enabled) => {
    if (enabled === props.feedbackTopic.enabled) return

    if (enabled) {
      emits('feedback-topic-enabled-updated', {
        feedbackTopicId: props.feedbackTopic.id,
        enabled: true,
      })
      return
    }

    localEnabled.value = false
    if (props.disableConfirmation) {
      confirmDisable()
    } else {
      openDialog()
    }
  })

  const confirmDisable = () => {
    emits('feedback-topic-enabled-updated', {
      feedbackTopicId: props.feedbackTopic.id,
      enabled: false,
    })
    closeDialog()
  }

  const cancelDisable = () => {
    localEnabled.value = true
    closeDialog()
  }
</script>
